/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext } from 'react';
import { useRouter } from 'next/router';
import { featureFlags } from '../constant/feature-flags';
import { getActiveFeatureFlagsFromUrl } from '../helpers/feature-flags';
import { FeatureFlags } from '../types/feature-flags';

/**
 * Uses URL querystring parameters to set context values for checking which feature flags are enabled. Allowing a dedicated
 * place for this to be controlled means functionality for determining feature flags status can be grouped here instead
 * of around the code base.
 */

const validFeatureFlags = Object.keys(featureFlags) as FeatureFlags[];

interface Context {
  validFeatureFlags: FeatureFlags[];
  activeFeatureFlags: FeatureFlags[] | [];
  isFeatureEnabled: (flag: FeatureFlags) => boolean;
}

const FeatureFlagContext = createContext<Context>({
  validFeatureFlags,
  activeFeatureFlags: [],
  isFeatureEnabled: () => false,
});

const FeatureFlagProvider = ({ children }: React.PropsWithChildren) => {
  const { query } = useRouter();

  const activeFeatureFlags = getActiveFeatureFlagsFromUrl(featureFlags, query);

  return (
    <FeatureFlagContext.Provider
      value={{
        activeFeatureFlags,
        validFeatureFlags,
        isFeatureEnabled: (flag) => {
          return activeFeatureFlags.includes(flag);
        },
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

const useFeatureFlagProvider = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagProvider must be used within a FeatureFlagProvider');
  }
  return context;
};

export { FeatureFlagProvider, useFeatureFlagProvider, FeatureFlagContext };
